import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box } from "@mui/material";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import StepperControls from "@components/StepperControls/StepperControls";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import { useNotification } from "@context/NotificationContext";
import type { SubmitState } from "@services/drybulkCargoOperationsApi";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  SUBMITTABLE_STATUS,
} from "../constants";
import { useDeleteCargoOperation } from "../hooks/useDeleteCargoOperation";
import { useSubmitOperationFlow } from "../hooks/useSubmitOperationFlow";
import { problemStatus } from "../utils/problemDetail";

interface DraftActionsProps {
  /** Null on the Add page before Add Total Quantity. */
  operationId: string | null;
  /** The status the server returned, compared and never derived. */
  status: string | null;
  /** Navigate to the operations list (the host decides the path). */
  onLeave: () => void;
  onSubmitted?: (state: SubmitState) => void;
  onDialogOpenChange?: (open: boolean) => void;
  /** Called by Save as draft first; resolves false when an open Total Quantity edit could not be saved. */
  beforeSaveDraft?: () => Promise<boolean>;
  /** DBCO-0030: called by the complete button first; resolves false to stop (nothing is prechecked or submitted). */
  beforeComplete?: () => Promise<boolean>;
  /**
   * DBCO-0065 D17/D20: Cancel soft-deletes the Draft (the grid's Delete) instead of only leaving, after asking for
   * confirmation while there are changes (`hasChanges`).
   * Set by the Add page, never by the page of a Draft reopened from the grid.
   */
  discardOnCancel?: boolean;
  /**
   * DBCO-0065 D18: Cancel on a Draft reopened from the grid undoes the visit; resolves true when the page may be
   * left. Ignored when `discardOnCancel` is set.
   */
  undoVisit?: () => Promise<boolean>;
  /**
   * DBCO-0065 D20: whether Cancel would remove anything (the new Draft on the Add page, the visit's saved or unsaved
   * changes on a reopened Draft). When true Cancel asks first, and the main navigation is guarded (D21).
   */
  hasChanges?: boolean;
  /** DBCO-0065 D24: the complete button's translation key (default: Submit). */
  completeLabelKey?: string;
}

export interface DraftActionsHandle {
  /** The same as pressing Cancel (the page's back arrow uses it on a Draft). */
  cancel: () => void;
}

const NAVIGATION_BLOCKER_SOURCE = "drybulk-cargo-operation-draft";

/**
 * DBCO-0065 (AC1-AC5) - the SA's three Draft actions in the platform's StepperControls bar: *Cancel*, *Save as
 * draft* and *Submit*. Auto-save is kept: *Save as draft* only confirms and leaves (saving an open, changed Total
 * Quantity edit first), *Cancel* discards just the change in progress by leaving. Before the operation exists, or
 * in any status but DRAFT, only *Cancel* shows. While a submit is pending, leaving is ignored.
 */
const DraftActions = forwardRef<DraftActionsHandle, DraftActionsProps>(function DraftActions(
  {
    operationId,
    status,
    onLeave,
    onSubmitted,
    onDialogOpenChange,
    beforeSaveDraft,
    beforeComplete,
    discardOnCancel = false,
    undoVisit,
    hasChanges = false,
    completeLabelKey = "stepper-controls.submit",
  },
  ref,
) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const flow = useSubmitOperationFlow({
    operationId: operationId ?? "",
    status,
    onDialogOpenChange,
    onSubmitted,
  });
  const discard = useDeleteCargoOperation({
    screen: "AddCargoOperation",
    successKey: `${DRYBULK_CARGO_OPS_LANG_BASE}.draft.discarded`,
  });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const isDraft = operationId !== null && status === SUBMITTABLE_STATUS;

  const performCancel = () => {
    if (flow.pending || savingRef.current) {
      return;
    }
    if (!discardOnCancel || !isDraft) {
      if (undoVisit && isDraft) {
        savingRef.current = true;
        setSaving(true);
        void undoVisit().then((leave) => {
          if (leave) {
            onLeave();
            return;
          }
          savingRef.current = false;
          setSaving(false);
        });
        return;
      }
      onLeave();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    discard.mutate(operationId, {
      onSuccess: onLeave,
      onError: (error) => {
        savingRef.current = false;
        setSaving(false);
        if (problemStatus(error) === 404) {
          onLeave();
        } else if (problemStatus(error) === 409) {
          void queryClient.invalidateQueries({
            queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId),
          });
        }
      },
    });
  };

  const [confirming, setConfirming] = useState(false);
  const changesTexts = discardOnCancel
    ? `${DRYBULK_CARGO_OPS_LANG_BASE}.draft.discard-dialog`
    : `${DRYBULK_CARGO_OPS_LANG_BASE}.draft.changes-dialog`;

  const requestCancel = () => {
    if (flow.pending || savingRef.current) {
      return;
    }
    if (!hasChanges || !isDraft) {
      performCancel();
      return;
    }
    setConfirming(true);
    onDialogOpenChange?.(true);
  };
  const closeConfirm = () => {
    setConfirming(false);
    onDialogOpenChange?.(false);
  };
  useImperativeHandle(ref, () => ({ cancel: requestCancel }));

  // D21: the platform guard covers the main menu and sidebar. Its confirm is synchronous and the navigation goes on
  // at once, so it only starts the discard or undo and the result notice comes through the global notifications.
  const { setNavigationBlocker } = useNavigationGuard();
  const backgroundCancel = useRef<() => void>(() => undefined);
  backgroundCancel.current = () => {
    if (discardOnCancel && operationId) {
      discard.mutate(operationId);
    } else if (undoVisit) {
      void undoVisit();
    }
  };
  const blockerActive = hasChanges && isDraft;
  useEffect(() => {
    setNavigationBlocker(
      NAVIGATION_BLOCKER_SOURCE,
      blockerActive
        ? {
            title: t(`${changesTexts}.title`),
            description: t(`${changesTexts}.description`),
            cancelButtonText: t(`${DRYBULK_CARGO_OPS_LANG_BASE}.draft.stay`),
            confirmButtonText: t(`${changesTexts}.confirm`),
            onConfirm: () => backgroundCancel.current(),
          }
        : null,
    );
    return () => setNavigationBlocker(NAVIGATION_BLOCKER_SOURCE, null);
  }, [blockerActive, changesTexts, setNavigationBlocker, t]);

  const complete = async () => {
    if (savingRef.current) {
      return;
    }
    if (beforeComplete) {
      savingRef.current = true;
      setSaving(true);
      try {
        if (!(await beforeComplete())) {
          return;
        }
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    }
    flow.onSubmit();
  };

  const saveDraft = async () => {
    if (flow.pending || savingRef.current) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      if (beforeSaveDraft && !(await beforeSaveDraft())) {
        return;
      }
      notify({ type: "success", title: t(`${DRYBULK_CARGO_OPS_LANG_BASE}.draft.saved`) });
      void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
      onLeave();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Box data-testid="drybulk-cargo-operation-actions">
      <StepperControls
        cancel={requestCancel}
        save={isDraft ? () => void saveDraft() : undefined}
        savePending={saving}
        complete={isDraft && flow.canSubmit ? () => void complete() : undefined}
        completePending={flow.pending || saving}
        completeLabelTranslationKey={completeLabelKey}
      />
      {flow.dialog}
      <GenericDialog
        open={confirming}
        testId="drybulk-cargo-operation-cancel-dialog"
        title={t(`${changesTexts}.title`)}
        content={t(`${changesTexts}.description`)}
        cancelButtonText={t(`${DRYBULK_CARGO_OPS_LANG_BASE}.draft.stay`)}
        confirmButtonText={t(`${changesTexts}.confirm`)}
        handleCloseDialog={closeConfirm}
        confirmAction={() => {
          closeConfirm();
          performCancel();
        }}
      />
    </Box>
  );
});

export default DraftActions;
